/**
 * Console Protection Utility
 * Prevents Self-XSS attacks similar to Facebook's implementation
 * 
 * Protects against:
 * - Malicious code injection via console
 * - Social engineering attacks
 * - Credential theft attempts
 */

class ConsoleProtection {
    constructor() {
        this.isWarningShown = false;
        this.originalConsole = { ...console };
        this.warningCount = 0;
        this.maxWarnings = 3;
        this.isProductionMode = process.env.NODE_ENV === 'production';
        
        this.init();
    }

    init() {
        if (!this.isProductionMode) {
            // Only enable in production to allow development
            return;
        }

        this.detectConsoleOpen();
        this.overrideConsoleMethods();
        this.addKeyboardProtection();
        this.showInitialWarning();
    }

    /**
     * Show warning message similar to Facebook
     */
    showWarning() {
        if (this.warningCount >= this.maxWarnings) {
            this.disableConsole();
            return;
        }

        const styles = {
            stop: 'color: #ff0000; font-size: 50px; font-weight: bold; text-shadow: 2px 2px 4px rgba(0,0,0,0.5);',
            warning: 'color: #ff4444; font-size: 16px; font-weight: bold; line-height: 1.5;',
            info: 'color: #666666; font-size: 14px; line-height: 1.4;',
            link: 'color: #1877f2; font-size: 14px; font-weight: bold;'
        };

        this.originalConsole.log('%cStop!', styles.stop);
        this.originalConsole.log(
            '%cThis is a browser feature intended for developers. If someone told you to copy and paste something here to enable an AMS feature or "hack" someone\'s account, it is a scam and will give them access to your AMS account.',
            styles.warning
        );
        this.originalConsole.log(
            '%cIf you are a developer working on AMS, you can disable this protection by setting localStorage.setItem("ams_dev_mode", "true")',
            styles.info
        );
        this.originalConsole.log(
            '%cFor more information about console security: https://en.wikipedia.org/wiki/Self-XSS',
            styles.link
        );
        this.originalConsole.log(' ');

        this.warningCount++;
        this.isWarningShown = true;
    }

    /**
     * Show initial warning when page loads
     */
    showInitialWarning() {
        // Delay to ensure console is ready
        setTimeout(() => {
            if (this.isConsoleOpen()) {
                this.showWarning();
            }
        }, 1000);
    }

    /**
     * Detect if developer console is open
     */
    detectConsoleOpen() {
        let devtools = { open: false, orientation: null };
        
        setInterval(() => {
            if (this.isConsoleOpen()) {
                if (!devtools.open) {
                    devtools.open = true;
                    this.onConsoleOpen();
                }
            } else {
                devtools.open = false;
            }
        }, 500);
    }

    /**
     * Check if console is currently open
     */
    isConsoleOpen() {
        const threshold = 160;
        return (
            window.outerHeight - window.innerHeight > threshold ||
            window.outerWidth - window.innerWidth > threshold
        );
    }

    /**
     * Called when console is opened
     */
    onConsoleOpen() {
        if (!this.isDeveloperMode()) {
            this.showWarning();
            
            // Additional protection: clear console after warning
            setTimeout(() => {
                if (this.originalConsole.clear) {
                    this.originalConsole.clear();
                    this.showWarning();
                }
            }, 2000);
        }
    }

    /**
     * Check if developer mode is enabled
     */
    isDeveloperMode() {
        return localStorage.getItem('ams_dev_mode') === 'true';
    }

    /**
     * Override console methods to show warnings
     */
    overrideConsoleMethods() {
        if (this.isDeveloperMode()) {
            return;
        }

        const protectedMethods = ['log', 'warn', 'error', 'info', 'debug'];
        
        protectedMethods.forEach(method => {
            if (console[method]) {
                console[method] = (...args) => {
                    this.showWarning();
                    
                    // Allow the original call for debugging
                    if (this.shouldAllowConsoleCall(args)) {
                        this.originalConsole[method](...args);
                    }
                };
            }
        });
    }

    /**
     * Determine if console call should be allowed
     */
    shouldAllowConsoleCall(args) {
        // Allow React development warnings and errors
        const allowedPatterns = [
            'Warning:', 
            'Error:', 
            '[HMR]',
            'React',
            '%c',  // Styled console logs (like our warnings)
        ];

        const stringArgs = args.map(arg => String(arg)).join(' ');
        return allowedPatterns.some(pattern => stringArgs.includes(pattern));
    }

    /**
     * Disable console completely after too many warnings
     */
    disableConsole() {
        const disabledMessage = () => {
            this.originalConsole.log(
                '%cConsole access has been disabled for security reasons.\n' +
                'If you are a developer, set localStorage.setItem("ams_dev_mode", "true") to re-enable.',
                'color: #ff0000; font-size: 16px; font-weight: bold;'
            );
        };

        // Override all console methods
        Object.keys(console).forEach(method => {
            if (typeof console[method] === 'function') {
                console[method] = disabledMessage;
            }
        });
    }

    /**
     * Add keyboard shortcut protection (F12, Ctrl+Shift+I, etc.)
     * Focus on console and network tabs only, not general right-click
     */
    addKeyboardProtection() {
        document.addEventListener('keydown', (e) => {
            if (this.isDeveloperMode()) {
                return;
            }

            // F12 - Opens DevTools (Console/Network)
            if (e.keyCode === 123) {
                e.preventDefault();
                this.showKeyboardWarning('F12');
                return false;
            }

            // Ctrl+Shift+I - Opens DevTools (Console/Network)
            if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
                e.preventDefault();
                this.showKeyboardWarning('Ctrl+Shift+I');
                return false;
            }

            // Ctrl+Shift+J - Opens Console directly
            if (e.ctrlKey && e.shiftKey && e.keyCode === 74) {
                e.preventDefault();
                this.showKeyboardWarning('Ctrl+Shift+J');
                return false;
            }

            // Ctrl+Shift+C - Opens Elements inspector (can access Console)
            if (e.ctrlKey && e.shiftKey && e.keyCode === 67) {
                e.preventDefault();
                this.showKeyboardWarning('Ctrl+Shift+C');
                return false;
            }

            // Ctrl+Shift+E - Opens Network tab (Firefox)
            if (e.ctrlKey && e.shiftKey && e.keyCode === 69) {
                e.preventDefault();
                this.showKeyboardWarning('Ctrl+Shift+E');
                return false;
            }

            // Note: We removed Ctrl+U blocking and right-click blocking
            // Users can view source and right-click normally for better UX
        });

        // NO RIGHT-CLICK BLOCKING - Users can right-click normally
        // We only protect console/network access through keyboard shortcuts
        // and direct console detection when DevTools opens
    }

    /**
     * Show warning for keyboard attempts
     */
    showKeyboardWarning(shortcut = 'Developer Tools') {
        // Show in-page notification instead of console
        this.showInPageWarning(shortcut);
    }

    /**
     * Show warning notification in the page UI
     */
    showInPageWarning(shortcut = 'Developer Tools') {
        // Check if warning already exists
        if (document.getElementById('ams-security-warning')) {
            return;
        }

        const warningDiv = document.createElement('div');
        warningDiv.id = 'ams-security-warning';
        warningDiv.innerHTML = `
            <div style="
                position: fixed;
                top: 20px;
                right: 20px;
                background: linear-gradient(135deg, #ff4444, #cc0000);
                color: white;
                padding: 20px;
                border-radius: 8px;
                box-shadow: 0 4px 20px rgba(0,0,0,0.3);
                z-index: 10000;
                max-width: 400px;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                border: 2px solid #fff;
            ">
                <div style="display: flex; align-items: center; margin-bottom: 10px;">
                    <span style="font-size: 24px; margin-right: 10px;">🛡️</span>
                    <strong style="font-size: 18px;">Security Notice</strong>
                </div>
                <p style="margin: 10px 0; line-height: 1.4;">
                    The shortcut <strong>${shortcut}</strong> has been disabled to prevent access to Developer Console and Network monitoring for security reasons.
                </p>
                <button onclick="this.parentElement.parentElement.remove()" style="
                    background: white;
                    color: #cc0000;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 4px;
                    font-weight: bold;
                    cursor: pointer;
                    margin-top: 10px;
                ">
                    OK
                </button>
            </div>
        `;

        document.body.appendChild(warningDiv);

        // Auto-remove after 5 seconds (shorter for better UX)
        setTimeout(() => {
            const warning = document.getElementById('ams-security-warning');
            if (warning) {
                warning.remove();
            }
        }, 5000);
    }

    /**
     * Enable developer mode (for legitimate developers)
     */
    static enableDeveloperMode() {
        localStorage.setItem('ams_dev_mode', 'true');
        console.log('✅ AMS Developer mode enabled. Console protection disabled.');
        console.log('To disable developer mode: localStorage.removeItem("ams_dev_mode")');
        
        // Reload to apply changes
        // eslint-disable-next-line no-restricted-globals
        if (confirm('Developer mode enabled. Reload page to apply changes?')) {
            window.location.reload();
        }
    }

    /**
     * Disable developer mode
     */
    static disableDeveloperMode() {
        localStorage.removeItem('ams_dev_mode');
        console.log('🛡️ AMS Developer mode disabled. Console protection enabled.');
        
        // Reload to apply changes
        // eslint-disable-next-line no-restricted-globals
        if (confirm('Developer mode disabled. Reload page to apply changes?')) {
            window.location.reload();
        }
    }
}

// Initialize console protection
const consoleProtection = new ConsoleProtection();

// Export for manual control
window.AMSConsoleProtection = {
    enableDeveloperMode: ConsoleProtection.enableDeveloperMode,
    disableDeveloperMode: ConsoleProtection.disableDeveloperMode,
    isDeveloperMode: () => localStorage.getItem('ams_dev_mode') === 'true'
};

export default ConsoleProtection; 